// File: frontend/src/services/adminCustomTopicService.ts

import { apiClient } from "./apiClient";
import { CustomTopicModel, TopicChannelRef } from "../models/CustomTopicModel";

// ── Types ──────────────────────────────────────────────────────

export type SuggestionStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface CustomTopicSuggestionDto {
  id: number;
  title: string;
  suggestedByUserId: number | null;
  suggestedByUsername: string | null;
  status: SuggestionStatus;
  reviewedByUsername: string | null;
  reviewedAt: string | null;
  // Optional note from the reviewing admin/mod (mainly on rejections)
  adminNote: string | null;
  // Title actually published on approval — differs from title when the admin edited it
  publishedTitle: string | null;
  // Ordered channels ("kanal") picked by the suggesting user — up to 10,
  // index 0 is the primary channel. Empty when none.
  channels: TopicChannelRef[];
  // NEW channel names proposed by the user that don't exist in master data
  // yet — created on approval if the admin keeps them. Empty when none.
  newChannelNames: string[];
  createdAt: string;
}

export interface CustomTopicPageResponse {
  content: CustomTopicModel[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

export interface SuggestionPageResponse {
  content: CustomTopicSuggestionDto[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

const BASE_URL = "/api/admin/custom-topics";

// ── Suggestions ────────────────────────────────────────────────

export interface GetSuggestionsParams {
  page?: number;
  size?: number;
  status?: SuggestionStatus | "";
  categoryId?: number | "";
  suggestedByUsername?: string;
  // ISO yyyy-MM-dd (from <input type="date">) — "Önerilen Tarih" range (createdAt)
  dateFrom?: string;
  dateTo?: string;
  // ISO yyyy-MM-dd (from <input type="date">) — "Kabul Tarihi" range (reviewedAt)
  reviewedDateFrom?: string;
  reviewedDateTo?: string;
  // "id" | "title" | "suggestedByUsername" | "reviewedAt" | "daysDiff" | "createdAt" (default)
  sortBy?: string;
  sortDir?: string;
}

/**
 * Get paginated suggestion list, optionally filtered by status, category,
 * suggester username, suggested-date range, and reviewed-date range.
 */
export const getSuggestions = async (
  params: GetSuggestionsParams = {}
): Promise<SuggestionPageResponse> => {
  const {
    page = 0, size = 20, status, categoryId, suggestedByUsername,
    dateFrom, dateTo, reviewedDateFrom, reviewedDateTo,
    sortBy = "createdAt", sortDir = "desc",
  } = params;
  const qs = new URLSearchParams();
  qs.append("page", page.toString());
  qs.append("size", size.toString());
  qs.append("sortBy", sortBy);
  qs.append("sortDir", sortDir);
  if (status) qs.append("status", status);
  // categoryId=0 is a deliberate value ("Kanalsız" — no category), not "unset" —
  // a plain truthy check would silently drop it, so compare against "" instead.
  if (categoryId !== undefined && categoryId !== "") qs.append("categoryId", categoryId.toString());
  if (suggestedByUsername) qs.append("suggestedByUsername", suggestedByUsername);
  if (dateFrom) qs.append("dateFrom", dateFrom);
  if (dateTo) qs.append("dateTo", dateTo);
  if (reviewedDateFrom) qs.append("reviewedDateFrom", reviewedDateFrom);
  if (reviewedDateTo) qs.append("reviewedDateTo", reviewedDateTo);
  return apiClient.get<SuggestionPageResponse>(`${BASE_URL}/suggestions?${qs.toString()}`);
};

/**
 * Get the number of PENDING suggestions (badge on the admin tab).
 */
export const getPendingSuggestionCount = async (): Promise<number> => {
  const res = await apiClient.get<{ pendingCount: number }>(`${BASE_URL}/suggestions/pending-count`);
  // Every fresh value is broadcast, so the header alert and the admin tab
  // badge stay in sync no matter which of them triggered the request
  // (e.g. the count drops right after an approve/reject in the admin panel).
  broadcastPendingSuggestionCount(res.pendingCount);
  return res.pendingCount;
};

/**
 * Window event fired whenever a fresh pending-suggestion count is known.
 * detail = the count (number). Listened to by the header alert.
 */
export const PENDING_SUGGESTION_COUNT_EVENT = "admin:pending-suggestion-count";

const broadcastPendingSuggestionCount = (count: number): void => {
  window.dispatchEvent(new CustomEvent<number>(PENDING_SUGGESTION_COUNT_EVENT, { detail: count }));
};

/**
 * Approve a suggestion — optionally with an edited title.
 * Returns the newly published topic.
 */
export const approveSuggestion = async (
  id: number,
  editedTitle?: string,
  channelIds?: number[] | null,
  newChannelNames?: string[] | null
): Promise<CustomTopicModel> => {
  return apiClient.post<CustomTopicModel>(`${BASE_URL}/suggestions/${id}/approve`, {
    editedTitle: editedTitle ?? null,
    // null keeps the suggestion's own channels; a list (even empty) overrides
    // them — order matters, index 0 becomes the primary channel
    channelIds: channelIds ?? null,
    // null keeps the suggestion's own proposed NEW channel names; a list
    // (even empty) overrides them — kept names are created in master data
    // (alphabetical position) and appended after channelIds on approval
    newChannelNames: newChannelNames ?? null,
  });
};

/**
 * Reject a suggestion (the row is kept as review history).
 * adminNote is an optional short note explaining the rejection.
 */
export const rejectSuggestion = async (id: number, adminNote?: string): Promise<void> => {
  await apiClient.post<void>(`${BASE_URL}/suggestions/${id}/reject`, {
    adminNote: adminNote ?? null,
  });
};

// ── Topics ─────────────────────────────────────────────────────

export interface GetAdminTopicsParams {
  page?: number;
  size?: number;
  search?: string;
  active?: "" | "true" | "false";
  categoryId?: number | "";
  suggestedByUsername?: string;
  createdByUsername?: string;
  // ISO yyyy-MM-dd (from <input type="date">) — "Kabul Tarihi" range (createdAt)
  dateFrom?: string;
  dateTo?: string;
  // ISO yyyy-MM-dd (from <input type="date">) — "Önerilen Tarih" range (originating
  // suggestion's createdAt)
  suggestedDateFrom?: string;
  suggestedDateTo?: string;
  locked?: "" | "true" | "false";
  // "id" | "title" | "entryCount" | "suggestedByUsername" | "createdByUsername" |
  // "suggestedAt" | "daysDiff" | "createdAt" (default)
  sortBy?: string;
  sortDir?: string;
}

/**
 * Get paginated topic list (includes inactive topics) with optional title
 * search, active/inactive filter, category filter, suggester/approver
 * username filter, lock status filter, and creation/suggested date ranges.
 */
export const getAdminTopics = async (
  params: GetAdminTopicsParams = {}
): Promise<CustomTopicPageResponse> => {
  const {
    page = 0, size = 20, search, active, categoryId,
    suggestedByUsername, createdByUsername, dateFrom, dateTo,
    suggestedDateFrom, suggestedDateTo, locked,
    sortBy = "createdAt", sortDir = "desc",
  } = params;
  const qs = new URLSearchParams();
  qs.append("page", page.toString());
  qs.append("size", size.toString());
  qs.append("sortBy", sortBy);
  qs.append("sortDir", sortDir);
  if (search) qs.append("search", search);
  if (active) qs.append("active", active);
  // categoryId=0 is a deliberate value ("Kanalsız" — no category), not "unset" —
  // a plain truthy check would silently drop it, so compare against "" instead.
  if (categoryId !== undefined && categoryId !== "") qs.append("categoryId", categoryId.toString());
  if (suggestedByUsername) qs.append("suggestedByUsername", suggestedByUsername);
  if (createdByUsername) qs.append("createdByUsername", createdByUsername);
  if (dateFrom) qs.append("dateFrom", dateFrom);
  if (dateTo) qs.append("dateTo", dateTo);
  if (suggestedDateFrom) qs.append("suggestedDateFrom", suggestedDateFrom);
  if (suggestedDateTo) qs.append("suggestedDateTo", suggestedDateTo);
  if (locked) qs.append("locked", locked);
  return apiClient.get<CustomTopicPageResponse>(`${BASE_URL}?${qs.toString()}`);
};

/**
 * Create a topic directly (no suggestion involved).
 * channelIds is an ordered list (index 0 = primary channel), up to 10.
 */
export const createTopic = async (title: string, channelIds?: number[] | null): Promise<CustomTopicModel> => {
  return apiClient.post<CustomTopicModel>(BASE_URL, { title, channelIds: channelIds ?? null });
};

/**
 * Update a topic: title (slug never changes), active flag, lock flag.
 */
export const updateTopic = async (
  id: number,
  // channelIds semantics: undefined = leave unchanged, [] = clear all channels,
  // otherwise = replace with this ordered list (index 0 = primary channel)
  updates: {
    title?: string;
    slug?: string;
    active?: boolean;
    isLocked?: boolean;
    lockReason?: string;
    lockReasonVisible?: boolean;
    channelIds?: number[];
  }
): Promise<CustomTopicModel> => {
  return apiClient.put<CustomTopicModel>(`${BASE_URL}/${id}`, updates);
};

/**
 * HARD delete a topic — ADMIN only, IRREVERSIBLE.
 * The backend physically removes the topic together with its entries, drafts,
 * votes, reports, pins and channel links. Nickaltı topics are rejected with
 * NICKALTI_TOPIC_PROTECTED. Always confirm with the user before calling this.
 */
export const deleteTopic = async (id: number): Promise<void> => {
  await apiClient.delete<void>(`${BASE_URL}/${id}`);
};